import React from "react";
import { registerExtension, EXTENSION_SORT_ORDER } from "@core/components/PipelineComponents/ExtensionProvider";
import { TooltipWrapper } from "@core/components/TooltipWrapper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-light-svg-icons";
import { Switch } from "@core/components/ui/switch";
import { Label } from "@core/components/ui/label";
import { useApi } from "@core/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, FormProvider, Controller } from "react-hook-form";

const ShippingComponent = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  const pluginName = "Shipping";
  const settingsGroup = "ShippingSettings";

  // Load settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ["shippingSettings", pluginName, settingsGroup],
    queryFn: () => api["/settings/{plugin}/{group}"](pluginName, settingsGroup),
  });

  const methods = useForm<any>({ 
    defaultValues: {
      ActiveProvider: settings?.SettingValues?.ActiveProvider ?? 0, // 0 = FlatRate
    }
  });

  // Update form when settings load
  React.useEffect(() => {
    if (settings?.SettingValues) {
      methods.reset({
        ActiveProvider: settings.SettingValues.ActiveProvider ?? 0,
      });
    }
  }, [settings, methods]);

  // Mutation to save settings
  const saveSettingsMutation = useMutation({
    mutationFn: async (data: any) => {
      await api["/settings/{plugin}/{group}/update"](pluginName, settingsGroup, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shippingSettings", pluginName, settingsGroup] });
    },
  });

  const handleProviderToggle = (providerType: number, checked: boolean) => {
    // Send all properties but set DefaultShippingOrigin to null to avoid DateTime binding issues
    // The pipeline expects all properties to be present, so we can't omit it entirely
    const updatedData = {
      ActiveProvider: checked ? providerType : (providerType === 0 ? 1 : 0), // If disabling, switch to the other
      DefaultShippingOrigin: null, // Set to null to avoid DateTime binding errors from nested properties
    };
    saveSettingsMutation.mutate(updatedData);
  };

  if (settingsLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Shipping</h3>
        <div className="text-sm text-muted-foreground">Loading settings...</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Shipping</h3>
        <div className="text-sm text-muted-foreground">Settings not available.</div>
      </div>
    );
  }

  // Use form state (with a default of 0 = FlatRate) so Flat Rate is enabled by default
  const activeProvider = methods.watch("ActiveProvider") ?? 0;
  const flatRateEnabled = activeProvider === 0;
  const fedExEnabled = activeProvider === 1;
  const upsEnabled = activeProvider === 2;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Shipping</h3>
      <FormProvider {...methods}>
        <div className="space-y-4">
          {/* Flat Rate Toggle */}
          <div className="flex items-center space-x-2">
            <Controller
              name="ActiveProvider"
              control={methods.control}
              defaultValue={0}
              render={({ field }) => (
                <Switch
                  id="flatRateShipping"
                  checked={flatRateEnabled}
                  onCheckedChange={(checked) => {
                    field.onChange(checked ? 0 : 1);
                    handleProviderToggle(0, checked);
                  }}
                  disabled={saveSettingsMutation.isPending}
                />
              )}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="flatRateShipping" className="cursor-pointer">
                Enable flat-rate shipping
              </Label>
              <TooltipWrapper
                tooltipContent="Apply a flat rate for shipping charge"
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="text-muted-foreground cursor-help"
                />
              </TooltipWrapper>
            </div>
          </div>

          {/* FedEx Toggle */}
          <div className="flex items-center space-x-2">
            <Controller
              name="ActiveProvider"
              control={methods.control}
              defaultValue={0}
              render={({ field }) => (
                <Switch
                  id="fedExShipping"
                  checked={fedExEnabled}
                  onCheckedChange={(checked) => {
                    field.onChange(checked ? 1 : 0);
                    handleProviderToggle(1, checked);
                  }}
                  disabled={saveSettingsMutation.isPending}
                />
              )}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="fedExShipping" className="cursor-pointer">
                Enable FedEx shipping
              </Label>
              <TooltipWrapper
                tooltipContent="Calculate shipping rates using FedEx API"
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="text-muted-foreground cursor-help"
                />
              </TooltipWrapper>
            </div>
          </div>

          {/* UPS Toggle */}
          <div className="flex items-center space-x-2">
            <Controller
              name="ActiveProvider"
              control={methods.control}
              defaultValue={0}
              render={({ field }) => (
                <Switch
                  id="upsShipping"
                  checked={upsEnabled}
                  onCheckedChange={(checked) => {
                    field.onChange(checked ? 2 : 0);
                    handleProviderToggle(2, checked);
                  }}
                  disabled={saveSettingsMutation.isPending}
                />
              )}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="upsShipping" className="cursor-pointer">
                Enable UPS shipping
              </Label>
              <TooltipWrapper
                tooltipContent="Calculate shipping rates using UPS API"
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="text-muted-foreground cursor-help"
                />
              </TooltipWrapper>
            </div>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

registerExtension(
  "admin.system.sitesetup.siteconfiguration",
  ShippingComponent,
  "Shipping",
  false,
  EXTENSION_SORT_ORDER.HIGH + 200  // Display after Catalog (HIGH + 175)
);
